import React from 'react';
import { Box, Typography, Button, Paper, Stack, Alert, AlertTitle } from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

/**
 * Top-level ErrorBoundary with a friendly fallback.
 *
 * Shows the error message, offers Reload / Go home / Copy details, and
 * prints the stack trace in an expandable panel for engineers. The
 * "Go home" action bypasses the React router and resets to /dashboard
 * via location.assign, so a broken router state can't trap the user.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Component error:', error, errorInfo);
  }

  handleCopy = async () => {
    try {
      const text = [
        this.state.error?.message,
        '',
        this.state.error?.stack,
        '',
        this.state.errorInfo?.componentStack,
      ].filter(Boolean).join('\n');
      await navigator.clipboard.writeText(text);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch {
      /* ignore clipboard failure */
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const stack = this.state.error?.stack || '';
    const componentStack = this.state.errorInfo?.componentStack || '';

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.default',
        }}
      >
        <Paper
          elevation={2}
          sx={{ maxWidth: 560, width: '100%', p: { xs: 3, sm: 4 }, borderRadius: 3 }}
        >
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'error.light',
                color: 'error.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ErrorIcon sx={{ fontSize: 36 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The page ran into an unexpected problem. You can reload to try
              again, or head back to the dashboard. Your work in other tabs
              is unaffected.
            </Typography>

            {this.state.error?.message && (
              <Alert severity="error" variant="outlined" sx={{ width: '100%', textAlign: 'left' }}>
                <AlertTitle>Error</AlertTitle>
                <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace', wordBreak: 'break-word' }}>
                  {this.state.error.message}
                </Typography>
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => window.location.reload()}
                fullWidth
              >
                Reload
              </Button>
              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={() => { window.location.assign('/dashboard'); }}
                fullWidth
              >
                Back to dashboard
              </Button>
              <Button
                variant="text"
                startIcon={<CopyIcon />}
                onClick={this.handleCopy}
                fullWidth
              >
                {this.state.copied ? 'Copied' : 'Copy details'}
              </Button>
            </Stack>

            {(stack || componentStack) && (
              <Box
                component="details"
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  p: 1.5,
                  '& summary': { cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
                }}
              >
                <summary>Technical details</summary>
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: '0.72rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    mt: 1,
                  }}
                >
                  {stack}
                  {componentStack ? `\n\nComponent stack:${componentStack}` : ''}
                </Typography>
              </Box>
            )}
          </Stack>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
